import { useCallback, useEffect, useState } from 'react';
import './style/ArenaQuestion.css';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import TableBlock from '../components/TableBlock';
import ToolsPanel from '../components/ToolsPanel';
import { authFetch } from '../authFetch';

const TOPIC_RATING_KEY = {
  algebra: 'algebraRating',
  advanced_math: 'advancedMathRating',
  geometry_and_trigonometry: 'geometryAndTrigonometryRating',
  problem_solving_and_data_analysis: 'problemSolvingAndDataAnalysisRating',
};

function ArenaQuestion(props) {
  const [pageLoading, setPageLoading] = useState(true);
  const [answerClass, setAnswerClass] = useState(['', '', '', '', '']);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [hintClass, setHintClass] = useState(['available', '', '']);
  const [selectedHint, setSelectedHint] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loadCounter, setLoadCounter] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [rating, setRating] = useState(null);
  const [pointsEarned, setPointsEarned] = useState(null);
  const [pointsLost, setPointsLost] = useState(null);
  const [desmosReady, setDesmosReady] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [hintLoading, setHintLoading] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [initialPointsLoaded, setInitialPointsLoaded] = useState(false);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);

  const handleAnswerClick = (index) => {
    setSelectedAnswer(index);
    setAnswerClass(prev => prev.map((_, i) => i === index ? 'selected' : ''));
  };

  const handleNextClick = () => {
    setPageLoading(true);
    setAnswerClass(['', '', '', '', '']);
    setSelectedAnswer(null);
    setHintClass(['available', '', '']);
    setSelectedHint(null);
    setShowExplanation(false);
    setChecked(false);
    setCheckLoading(false);
    setHintLoading(false);
    setSkipped(false);
    setRating(null);
    setPointsEarned(null);
    setPointsLost(null);
    setInitialPointsLoaded(false);
    setLoadCounter(c => c + 1);
  }

  const handleSkipClick = async () => {
    if (!questionData) return;
    setCheckLoading(true);
    setPointsEarned(null);
    setPointsLost(null);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/arenaQuestion/handleSkipClick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId: questionData.question_id, topic: props.sql })
      });
      const data = await response.json();
      if (data.success) {
        setPointsLost(data.pointsLost);
        setRating(data.newRating);
        if (questionData.question_type === 'FRQ') {setAnswerClass(['', '', '', '', 'skipped']);} 
        else {setAnswerClass(['skipped', 'skipped', 'skipped', 'skipped', '']);}
        setChecked(true);
        setSkipped(true);
        setCheckLoading(false);
      }
    }
    catch (error) {
      console.error('Error:', error);
      setCheckLoading(false);
    }
  }

  const handleCheckClick = useCallback(async () => {
    if (selectedAnswer === null || !questionData) { return; }
    setCheckLoading(true);
    setPointsEarned(null);
    setPointsLost(null);
    try {
      const response = await authFetch(`${process.env.REACT_APP_API_BASE}/arenaQuestion/handleCheckClick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId: questionData.question_id, selectedAnswer, topic: props.sql })
      });
      const data = await response.json();
      if (data.success) {
        setPointsEarned(data.pointsEarned);
        setPointsLost(data.pointsLost);
        setRating(data.newRating);
        setQuestionData(prev => ({ ...prev, explanation: data.explanation, correct_answer: data.correctAnswer }));
        if (questionData.question_type === 'FRQ') {setAnswerClass(prev => prev.map((_, i) => i === 4 ? (data.isCorrect ? 'correct' : 'incorrect') : ''));}
        else {
          const correctIndex = parseInt(data.correctAnswer);
          if (data.isCorrect) {setAnswerClass(prev => prev.map((_, i) => i === selectedAnswer ? 'correct' : ''));}
          else {
            setAnswerClass(prev => {
              const temp = [...prev];
              temp[correctIndex] = 'correct';
              temp[selectedAnswer] = 'incorrect';
              return temp;
            });
          }
        }
        setChecked(true);
        setCheckLoading(false);
      }
    }
    catch (error) { 
      console.error('Error:', error);
      setCheckLoading(false);
    }
  }, [selectedAnswer, questionData, props.sql]);

  const handleHintClick = async (index) => {
    if (hintClass[index] === 'revealed') { setSelectedHint(index); return; }
    if (hintClass[index] === 'available') {
      setHintLoading(true);
      try {
        if (questionData) {
          await authFetch(`${process.env.REACT_APP_API_BASE}/arenaQuestion/handleHintClick`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ questionId: questionData.question_id })
          });
        }
        setPointsEarned(null);
        setPointsLost(null);
        setHintClass(prev => {
          const next = [...prev];
          next[index] = 'revealed';
          if (index + 1 < next.length) next[index + 1] = 'available';
          return next;
        });
        setSelectedHint(index);
      }
      catch (error) { 
        console.error('Error:', error);
        setHintLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadQuestion = async () => {
      try {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE}/arenaQuestion/loadQuestion`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            topic: props.sql,
          }),
        });
        const data = await response.json();
        if (data.question) {
          setQuestionData(data.question);
          const h = data.hintsUsed || 0;
          if (h === 0) { setHintClass(['available', '', '']); }
          else if (h === 1) { setHintClass(['revealed', 'available', '']); }
          else if (h === 2) { setHintClass(['revealed', 'revealed', 'available']); }
          else { setHintClass(['revealed', 'revealed', 'revealed']); }
        }
        else {console.log('error');}
      }
      catch (error) {
        console.error('Error:', error);
      }
    };
    const loadRating = async () => {
      try {
        const response = await authFetch(`${process.env.REACT_APP_API_BASE}/arena/loadRatings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        const data = await response.json();
        if (data.success) {
          const key = TOPIC_RATING_KEY[props.sql];
          setRating(data[key]);
        }
        else {console.log('error');}
      }
      catch (error) {
        console.error('Error:', error);
      }
    };
    const initialize = async () => {
      setPageLoading(true);
      setQuestionData(null);
      setRating(null);
      setPointsEarned(null);
      setPointsLost(null);
      await loadQuestion();
      await loadRating();
    };
    initialize();
  }, [loadCounter]);

  useEffect(() => {
    const loadPoints = async () => {
      if (checked || rating == null || !questionData || !questionData.difficulty) { return; }
      try {
        const hintsUsed = hintClass.filter(h => h === 'revealed').length;
        const response = await authFetch(`${process.env.REACT_APP_API_BASE}/arenaQuestion/loadPoints/${rating}/${questionData.difficulty}/${hintsUsed}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        const data = await response.json();
        if (data.success) {
          setPointsEarned(data.pointsEarned);
          setPointsLost(data.pointsLost);
          setInitialPointsLoaded(true);
          setHintLoading(false);
        }
      }
      catch (error) {
        console.error('Error:', error);
        setHintLoading(false);
      }
    };
    loadPoints();
  }, [checked, rating, questionData, hintClass]);

  useEffect(() => {
    const isReady = questionData && rating != null && desmosReady && initialPointsLoaded;
    setPageLoading(!isReady);
  }, [questionData, rating, desmosReady, initialPointsLoaded]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && selectedAnswer !== null && !checked) {
        e.preventDefault();
        e.stopPropagation();
        handleCheckClick();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedAnswer, checked, handleCheckClick]);

  return (
    <div className='arena-question-container'>
      <div className='mode-header'>
        <p>{props.topic}</p>
      </div>
      <div className={`main-layout ${toolsCollapsed ? 'tools-collapsed' : ''}`} style={pageLoading ? { visibility: 'hidden', position: 'absolute' } : undefined}>
        <ToolsPanel onReady={() => setDesmosReady(true)} resetKey={loadCounter} onCollapse={setToolsCollapsed} />
        <div className='question-content'>
          <div className='question-box'>
            <div className='question-info'>
              <p>{questionData ? `#${questionData.uuid}` : ''}</p>
              <p>{questionData ? questionData.difficulty : ''}</p>
            </div>
            <div className='question-body'>
              <div className='question-text'>
                <p>
                  {questionData ? (
                    questionData.content.map((block, i) => {
                      switch (block.type) {
                        case "text":
                          return <span key={i}>{block.content} </span>;
                        case "inline_math":
                          return <InlineMath key={i} math={block.content} />;
                        case "block_math":
                          return <BlockMath key={i} math={block.content} />;
                        case "image":
                          return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                        case "table":
                          return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                        case "line_break":
                          return <br key={i} />;
                        default:
                          return null;
                      }
                    })
                  ) : null}
                </p>
              </div>
              <div className='answer-options'>
                {checked && showExplanation && questionData && questionData.explanation ? (
                  <div className='explanation-content'>
                    {questionData.explanation.map((block, i) => {
                      switch (block.type) {
                        case "text":
                          return <span key={i}>{block.content}</span>;
                        case "inline_math":
                          return <InlineMath key={i} math={block.content} />;
                        case "block_math":
                          return <BlockMath key={i} math={block.content} />;
                        case "image":
                          return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                        case "table":
                          return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                        case "line_break":
                          return <br key={i} />;
                        default:
                          return null;
                      }
                    })}
                  </div>
                ) : questionData && questionData.question_type === 'FRQ' ? (
                <>
                  <input
                    type="text"
                    value={selectedAnswer === null ? '' : selectedAnswer}
                    onChange={(e) => setSelectedAnswer(e.target.value || null)}
                    placeholder=""
                    className={answerClass[4]}
                    disabled={checked}
                  />
                  {checked && questionData.correct_answer != null && <p className='correct-answer'>Correct Answer: {questionData.correct_answer}</p>}
                </>
                ) : (
                  <>
                    {['A', 'B', 'C', 'D'].map((label, index) => (
                      <button key={index} className={answerClass[index]} onClick={() => !checked && handleAnswerClick(index)} disabled={checked}>
                        <p>{`${label})`}</p>
                        <p style={{ textAlign: 'left' }}>
                          {questionData && questionData.answer_choices && questionData.answer_choices[index] ? (
                            questionData.answer_choices[index].map((block, i) => {
                              switch (block.type) {
                                case "text":
                                  return <span key={i}>{block.content}</span>;
                                case "inline_math":
                                  return <InlineMath key={i} math={block.content} />;
                                case "block_math":
                                  return <BlockMath key={i} math={block.content} />;
                                case "image":
                                  return <img key={i} src={`${process.env.REACT_APP_API_BASE}${block.src}`} alt={block.alt} />;
                                case "table":
                                  return <TableBlock key={i} headers={block.headers} rows={block.rows} />;
                                default:
                                  return null;
                              }
                            })
                          ) : ''}
                        </p>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className='hint-display'>
            {selectedHint !== null && hintClass[selectedHint] === 'revealed' && !hintLoading && !checked && questionData && questionData.hints && questionData.hints[selectedHint] ? (
              <div className='hint-content'>
                {questionData.hints[selectedHint].map((block, i) => {
                  switch (block.type) {
                    case "text":
                      return <span key={i}>{block.content}</span>;
                    case "inline_math":
                      return <InlineMath key={i} math={block.content} />;
                    case "block_math":
                      return <BlockMath key={i} math={block.content} />;
                    default:
                      return null;
                  }
                })}
              </div>
            ) : null}
          </div>
          <div className='action-controls'>
            <div className='hint-buttons'>
              {!skipped && (checked ? (
                showExplanation ? <button id='answers' onClick={() => setShowExplanation(false)}>Show Answers</button> : <button id='explanation' onClick={() => setShowExplanation(true)}>Explanation</button>
              ) : (
                Array.from({ length: 3 }).map((_, i) => {
                  return <button key={i} onClick={() => handleHintClick(i)} className={hintClass[i]}>Hint {i + 1}</button>;
                })
              ))}
            </div>
            <div className='navigation-buttons'>
              {checked ? (
                <>
                  {!skipped && <button id='regenerate-question' >Regenerate</button>}
                  <button id='next' onClick={handleNextClick}>Next</button>
                  <div className='rating-display'>
                    {pointsEarned == null && pointsLost == null ? (
                      <div className='rating-spinner' />
                    ) : (
                      <>
                        <p id='rating'>{rating ? rating : '0'}</p>
                        <div>
                          {answerClass.includes('incorrect') || skipped ? (
                            <p id='lost'>-{Math.round(pointsLost)}</p>
                          ) : (
                            <p id='earned'>+{Math.round(pointsEarned)}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {hintClass[0] === 'available' && !hintLoading && <button id='skip' onClick={handleSkipClick} disabled={checkLoading}>Skip</button>}
                  <button id='check' onClick={handleCheckClick} disabled={checkLoading || hintLoading}>Check</button>
                  <div className='rating-display'>
                    {checkLoading || hintLoading || pointsEarned == null || pointsLost == null ? (
                      <div className='rating-spinner' />
                    ) : (
                      <>
                        <p id='rating'>{rating ? rating : '0'}</p>
                        <div>
                          <p id='lost'>-{Math.round(pointsLost)}</p>
                          <p>/</p>
                          <p id='earned'>+{Math.round(pointsEarned)}</p>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {pageLoading && <div className='main-layout-loading'>
        <div className='three-circle-loader'>
          <span />
          <span />
          <span />
        </div>
      </div>}
    </div>
  );
}

export default ArenaQuestion;